import { useEffect, useState } from "react";
import { api } from "@/api/client";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";

/** Project Visibility V1A (2026-09) — one row's on/off switch in the Client page's "Manage
 * Client Portal" table. Backed by the Mongo-only `rm_client_project_visibility` side-table
 * (modules/client_portal.py's admin routes, gated `client_portal.admin`), same discipline as
 * ClientPortalPmCard — never a Notion field. "On" = visible to the client (default), "Off" =
 * hidden from every Client Portal surface for that project (home, list, direct link, and
 * every sub-resource — deliverables/documents/BTS/updates/messages/meetings).
 *
 * Perf fix (2026-09, NJM OS Phase-0 audit finding): this used to fetch its own visibility on
 * mount, one GET per row — N round trips for a client with N projects. `initialHidden` lets the
 * parent preload the whole table in ONE batched call and pass each row's value down; the
 * per-row GET only runs as a fallback when no preloaded value is given (any other, standalone
 * use of this component keeps working unchanged).
 *
 * Trust fix (2026-06, Tessolve "Previous projects" audit): a Lead/Proposed project rendered an
 * ON, clickable switch next to the words "Not visible (Lead)" — a control that contradicted its
 * own label, since the manual flag cannot change a status exclusion. When the server reports an
 * `exclusion_reason` (or, for an older payload, `statusExcluded`), the switch is now OFF and
 * DISABLED with a label that says WHY: "Not visible – pre-sale project" for Lead/Proposed,
 * "Not visible – project didn't proceed" for Lost. Never an active toggle next to a
 * not-visible label. */
const EXCLUSION_LABELS = {
  pre_sale: "Not visible – pre-sale project",
  not_proceeding: "Not visible – project didn't proceed",
};

export const ClientPortalVisibilityToggle = ({ projectId, initialHidden, statusExcluded, status, exclusionReason }) => {
  const preloaded = initialHidden !== undefined;
  const [visible, setVisible] = useState(preloaded ? !initialHidden : true);
  const [loading, setLoading] = useState(!preloaded);
  const [errored, setErrored] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fetchedStatusExcluded, setFetchedStatusExcluded] = useState(statusExcluded);
  const [fetchedReason, setFetchedReason] = useState(exclusionReason);

  useEffect(() => {
    if (preloaded) { setVisible(!initialHidden); setLoading(false); return; }
    setErrored(false);
    api.get(`/v1/client-portal/admin/projects/${projectId}/portal-visibility`)
      .then((r) => { setVisible(!r.data.hidden); setFetchedStatusExcluded(r.data.status_excluded); setFetchedReason(r.data.exclusion_reason); })
      .catch(() => setErrored(true))
      .finally(() => setLoading(false));
  }, [projectId, preloaded, initialHidden]);

  const toggle = async (nextVisible) => {
    setSaving(true);
    try {
      await api.put(`/v1/client-portal/admin/projects/${projectId}/portal-visibility`, { hidden: !nextVisible });
      setVisible(nextVisible);
      toast.success(nextVisible ? "Project is now visible in the Client Portal" : "Project hidden from the Client Portal");
    } catch {
      toast.error("Couldn't update Client Portal visibility");
    } finally { setSaving(false); }
  };

  if (loading) return null;

  if (errored) {
    return <span className="text-[11px] uppercase tracking-wider text-red-600" data-testid={`client-portal-visibility-error-${projectId}`}>Couldn't load</span>;
  }

  const excludedByStatus = statusExcluded !== undefined ? statusExcluded : fetchedStatusExcluded;
  const reason = exclusionReason !== undefined ? exclusionReason : fetchedReason;
  const lockedByStatus = !!reason || !!excludedByStatus;
  const label = lockedByStatus
    ? (EXCLUSION_LABELS[reason] || `Not visible – ${status || "status"}`)
    : visible ? "Visible" : "Hidden";

  return (
    <div className="flex items-center gap-2" data-testid={`client-portal-visibility-toggle-${projectId}`}>
      <Switch checked={lockedByStatus ? false : visible} disabled={saving || lockedByStatus} onCheckedChange={toggle} data-testid={`client-portal-visibility-switch-${projectId}`} />
      <span className={`text-[11px] uppercase tracking-wider ${lockedByStatus ? "text-amber-600" : "text-[#78716C]"}`} data-testid={`client-portal-visibility-label-${projectId}`}
        title={lockedByStatus ? `This project's status (${status || "pre-sale"}) keeps it out of the Client Portal — this switch can't change that` : undefined}>
        {label}
      </span>
    </div>
  );
};
